import { useCallback, useEffect, useMemo, useState } from 'react'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded'
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded'
import { useToast } from '../../context/useToast'
import { useNls } from '../../nls/nlsProvider'
import {
  findStockItemForBarcode,
  submitStockCountRound,
} from '../../services/stockCountSuggestionService'
import { useCountDrafts, type CountDraft } from '../../services/helper/helperCountDraftStore'
import type { StockItem } from '../../types/stock'
import { Button, IconButton, MiniLoader, NumberStepper, TextInput } from '../Common'
import { BarcodeScannerShell } from './BarcodeScannerShell'
import { HelperEmptyState } from './HelperEmptyState'
import { getSharedDeviceWorkRouterContext } from '../../services/sharedDeviceWorkRouter'
import {
  getActivePersonCache,
  subscribeActivePersonCache,
} from '../../services/sharedDevicePersonCache'

type HelperActor = { id: string; name: string } | null

const toCountDraft = (item: StockItem): CountDraft => ({
  stockItemId: item.id,
  sku: item.sku || item.id,
  barcode: item.barcode ?? '',
  name: item.name,
  countedQuantity: item.quantity,
  systemQuantityAtScan: item.quantity,
  note: '',
})

export const HelperStockCountTab = ({
  workspaceId,
  actor,
}: {
  workspaceId: string | null
  actor: HelperActor
}) => {
  const { t } = useNls()
  const { showToast } = useToast()
  const [scannerOpen, setScannerOpen] = useState(false)
  const [manualBarcode, setManualBarcode] = useState('')
  const [drafts, setDrafts] = useCountDrafts(actor?.id, workspaceId)
  const routerCtx = getSharedDeviceWorkRouterContext()
  const [cachedStockItems, setCachedStockItems] = useState<StockItem[]>(
    () => (routerCtx ? getActivePersonCache()?.snapshot?.stockItems ?? [] : []),
  )

  useEffect(() => {
    if (!routerCtx) return
    return subscribeActivePersonCache((snap) => {
      setCachedStockItems(snap?.stockItems ?? [])
    })
  }, [routerCtx])

  const [busy, setBusy] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const totalDelta = useMemo(
    () => drafts.reduce((sum, draft) => sum + (draft.countedQuantity - draft.systemQuantityAtScan), 0),
    [drafts],
  )

  const addBarcode = useCallback(async (barcode: string) => {
    if (!workspaceId || busy) return
    const value = barcode.trim()
    if (!value) return
    setBusy(true)
    try {
      const match = await findStockItemForBarcode(workspaceId, value)
      if (!match) {
        showToast(t('NLSHelperStockCountUnknownBarcode', { barcode: value }), 'error')
        return
      }
      setDrafts((current) => {
        const next = current.filter((entry) => entry.sku !== match.sku)
        return [toCountDraft(match), ...next]
      })
      setManualBarcode('')
      setScannerOpen(false)
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('NLSHelperStockCountLookupFailed'), 'error')
    } finally {
      setBusy(false)
    }
  }, [busy, setDrafts, showToast, t, workspaceId])

  const submitRound = async () => {
    if (!workspaceId || !actor || drafts.length === 0 || submitting) return
    setSubmitting(true)
    try {
      await submitStockCountRound({
        workspaceId,
        actor,
        suggestions: drafts.map((draft) => ({
          stockItemId: draft.stockItemId,
          sku: draft.sku,
          barcode: draft.barcode,
          countedQuantity: draft.countedQuantity,
          systemQuantityAtScan: draft.systemQuantityAtScan,
          note: draft.note,
        })),
      })
      setDrafts([])
      showToast(t('NLSHelperStockCountQueued'), 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('NLSHelperStockCountQueueFailed'), 'error')
    } finally {
      setSubmitting(false)
    }
  }

  if (!workspaceId) {
    return (
      <section className="flex-1 grid place-items-center rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-6 text-center">
        <p className="text-sm text-[var(--muted)]">{t('NLSHelperPickWorkspaceBeforeScan')}</p>
      </section>
    )
  }

  return (
    <section className="flex-1 flex flex-col gap-4 min-h-0" data-testid="helper-stock-count-tab">
      <div className="rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4 flex items-center gap-3">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-r from-[var(--btn-primary-from)] to-[var(--btn-primary-to)] text-[var(--on-accent)]">
          <PlaylistAddCheckRoundedIcon />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">{t('NLSHelperStockCountEyebrow')}</p>
          <h2 className="text-base font-semibold text-[var(--text-strong)] truncate">{t('NLSHelperStockCountTitle')}</h2>
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={() => setScannerOpen(true)}
        >
          {t('NLSHelperStockCountScan')}
        </Button>
      </div>

      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          void addBarcode(manualBarcode)
        }}
      >
        <TextInput
          value={manualBarcode}
          onChange={setManualBarcode}
          placeholder={t('NLSHelperStockCountManualPlaceholder')}
          wrapperClassName="flex-1 min-w-0"
        />
        <IconButton
          type="submit"
          variant="tertiary"
          size="lg"
          label={t('NLSHelperStockCountUseBarcodeAria')}
          icon={busy ? <MiniLoader size="xs" inline label="" /> : <QrCodeScannerRoundedIcon fontSize="small" />}
        />
      </form>

      {drafts.length === 0 ? (
        routerCtx && cachedStockItems.length > 0 ? (
          <div className="flex flex-col gap-3" data-testid="cached-stock-items-list">
            <p className="text-xs font-semibold text-[var(--muted)]">{t('NLSHelperStockCountTitle')}</p>
            {cachedStockItems.map((item) => (
              <article
                key={item.id}
                data-testid={`cached-stock-item-${item.id}`}
                className="rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4 flex items-center justify-between gap-3 cursor-pointer"
                onClick={() => {
                  setDrafts((current) => {
                    const next = current.filter((entry) => entry.sku !== item.sku)
                    return [toCountDraft(item), ...next]
                  })
                }}
              >
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-[var(--text-strong)]">{item.name}</h3>
                  <p className="mt-1 text-xs text-[var(--muted)]">{item.sku}{item.barcode ? ` - ${item.barcode}` : ''}</p>
                </div>
                <span className="text-xs font-medium text-[var(--muted)]">
                  {item.quantity} {item.unit}
                </span>
              </article>
            ))}
          </div>
        ) : (
          <HelperEmptyState
            title={t('NLSHelperStockCountEmptyTitle')}
            body={t('NLSHelperStockCountEmptyBody')}
          />
        )
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3 rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] px-4 py-3">
            <span className="text-xs font-semibold text-[var(--text-strong)]">{t('NLSHelperStockCountRoundSummary', { count: drafts.length, delta: totalDelta })}</span>
            <Button variant="primary" onClick={() => { void submitRound() }} disabled={!actor || submitting}>
              {submitting ? <MiniLoader size="xs" inline label={t('NLSHelperStockCountSubmitting')} /> : t('NLSHelperStockCountSubmit')}
            </Button>
          </div>
          {drafts.map((draft) => (
            <article key={draft.sku} className="rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-[var(--text-strong)]">{draft.name}</h3>
                  <p className="mt-1 text-xs text-[var(--muted)]">{draft.sku} - {draft.barcode}</p>
                </div>
                <IconButton
                  variant="tertiary"
                  size="md"
                  tone="danger"
                  label={t('NLSHelperStockCountRemoveAria', { name: draft.name })}
                  icon={<DeleteOutlineRoundedIcon fontSize="small" />}
                  onClick={() => setDrafts((current) => current.filter((entry) => entry.sku !== draft.sku))}
                />
              </div>
              <div className="mt-3 grid grid-cols-[1fr_7rem] gap-2">
                <label className="text-xs text-[var(--muted)]">
                  {t('NLSHelperStockCountNote')}
                  <TextInput
                    value={draft.note}
                    onChange={(next) => setDrafts((current) => current.map((entry) => entry.sku === draft.sku ? { ...entry, note: next } : entry))}
                    size="sm"
                    wrapperClassName="mt-1"
                  />
                </label>
                <label className="text-xs text-[var(--muted)]">
                  {t('NLSHelperStockCountCounted')}
                  <NumberStepper
                    min={0}
                    step={1}
                    value={draft.countedQuantity}
                    onChange={(next) => setDrafts((current) => current.map((entry) => entry.sku === draft.sku ? { ...entry, countedQuantity: Math.max(0, Math.floor(next || 0)) } : entry))}
                    size="sm"
                    className="mt-1"
                    ariaLabel={t('NLSHelperStockCountCounted')}
                  />
                </label>
              </div>
              <p className="mt-2 text-[11px] text-[var(--muted)]">{t('NLSHelperStockCountSystemQuantity', { count: draft.systemQuantityAtScan })}</p>
            </article>
          ))}
        </div>
      )}

      <BarcodeScannerShell
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={(barcode) => { void addBarcode(barcode) }}
        mode="COUNT"
      />
    </section>
  )
}
